// ABOUTME: Handles Zapier action operations - fetching needs, samples, and action info
// ABOUTME: Also handles controlled apps for enterprise access control
import {
  ActionInfoSchema,
  ActionSchema,
  ActionType,
  AgentsZapierFacade,
  ConnectionId,
  GivesRequest,
  InvokeRequest,
  NeedSchema,
  NeedsRequest,
  ZapierFacadeError,
} from ".";
import { implementationNeedsAuth } from "./authentications";
import { getCached } from "./cache";
import { getImplementation } from "./implementations";
import { components } from "./monolith-v4-api";
import {
  complexFlatten,
  formatParentKeyForLabel,
  prepareParamsForSend,
} from "./util";

/** These needs are skipped because they are not relevant to Agents */
const NEEDS_TO_SKIP = new Set([
  "zap_id",
  "zap_step_id",
  "zap_path_run_id",
  "idempotency_key",
  "_zap_search_success_on_miss",
]);

/**
 * Given an implementation, get the given action from it.
 *
 * This also handles "read_bulk" custom actions that are hidden
 */
const getActionFromImplementation = (
  implementation: components["schemas"]["Implementation"],
  action: string,
  actionType: ActionType,
): Partial<ActionInfoSchema> => {
  let actionToUse: components["schemas"]["Action"] | undefined;

  // bulk read actions don't exist in the implementation since they are usually hidden
  // UUIDs are "V3 External Actions" (V3EA) and are also generally hidden
  if (
    actionType === "read_bulk" ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      action,
    )
  ) {
    actionToUse = {
      key: action,
      type: actionType,
      name: action,
      noun: action,
      description: "",
    };
  } else {
    actionToUse = implementation.actions?.find(
      (a) => a.key === action && a.type === actionType,
    );
  }

  if (!actionToUse) {
    return { action: undefined, needs: undefined };
  }

  return {
    action: {
      ...actionToUse,
      app_name: implementation.name ?? "",
      display_name: `${implementation.name}: ${actionToUse.name}`,
      app_needs_auth: implementationNeedsAuth(implementation),
      selected_api: implementation.selected_api ?? "",
      versionless_selected_api:
        implementation.selected_api?.split("@")[0] ?? "",
    },

    // FIXME monolith types are wrong
    needs: actionToUse.needs as unknown as NeedSchema[],
  };
};

export const getNeeds = async (
  facade: AgentsZapierFacade,
  {
    selectedApi,
    action,
    actionType,
    params,
    authenticationId,
    invalidateCache = false,
  }: {
    selectedApi: string;
    action: string;
    actionType: ActionType;
    params: Record<string, unknown>;
    authenticationId?: ConnectionId;
    invalidateCache?: boolean;
  },
): Promise<NeedSchema[]> => {
  return getCached(
    facade.cache,
    "needs",
    JSON.stringify({
      selected_api: selectedApi,
      action,
      type_of: actionType,
      params,
      authentication_id: authenticationId,
    }),
    async () => {
      const requestPayload: NeedsRequest = {
        selected_api: selectedApi,
        action,
        type_of: actionType,
        params,
        authentication_id: authenticationId,
        invalidate_cache: invalidateCache,
      };

      const { data, error, response } = await facade.api.POST(
        "/api/v4/implementations/needs/",
        {
          body: requestPayload,
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (error) {
        throw new ZapierFacadeError(
          "/api/v4/implementations/needs/",
          "POST",
          requestPayload,
          response,
          error?.errors,
        );
      }

      if (!data.needs) {
        throw new Error("'needs' key not found in response");
      }

      // Skip needs that don't apply to Zapier Agents:
      // - '_zap_search_success_on_miss'
      // - Computed fields (zap_id, zap_step_id, etc.)
      // - 'copy' type fields: Only used for Zapier Editor UI help text
      let filteredNeeds = (data.needs || [])
        .filter((need) => need.type !== "copy" && !NEEDS_TO_SKIP.has(need.key))
        .map(({ help_text_html, ...rest }) => rest as NeedSchema);

      // 💩 Edge-case 💩: Tables by Zapier
      // Some custom fields (e.g. for URL columns) have parent_key but don't expect roll-up into line-items behavior.
      // So we strip them from the needs.
      if (selectedApi.startsWith("TableCLIAPI")) {
        if (["create_record", "update_record"].includes(action)) {
          // Find all the needs with a parent_key and:
          // - Remove the parent_key
          // - Set the `${parent_key} ($originalLabel)` as the label
          filteredNeeds = filteredNeeds.map((need) => {
            if (need.parent_key) {
              return {
                ...need,
                parent_key: undefined,
                label: `${formatParentKeyForLabel(need.parent_key)} (${
                  need.label
                })`,
              };
            }
            return need;
          });
        }
      }
      // 💩 Edge-case 💩: Google Mail
      // Add label action should expect new_label_ids to be a required field.
      if (selectedApi.startsWith("GoogleMailV2CLIAPI")) {
        if (action === "add_label") {
          filteredNeeds = filteredNeeds.map((need) => {
            if (need.key === "new_label_ids") {
              return { ...need, required: true };
            }
            return need;
          });
        }
      }
      // 💩 Edge-case 💩: Bubble
      // BubbleCLIAPI requires data_type before search_field can fetch choices
      if (selectedApi.startsWith("BubbleCLIAPI")) {
        filteredNeeds = filteredNeeds.map((need) => {
          if (need.key === "search_field") {
            return { ...need, depends_on: ["data_type"] };
          }
          return need;
        });
      }

      return filteredNeeds;
    },
    {
      invalidateCache,
    },
  );
};

/**
 * Get a sample of what the action would return
 *
 * WARNING: THIS IS SLOOOOOOOW!!!!
 */
export const getActionSample = async (
  facade: AgentsZapierFacade,
  params: {
    app: string;
    action: string;
    actionType: ActionType;
    // Numeric only: sampling runs against Conjurer, which does not share the
    // monolith's handling of a connection's public UUID.
    authenticationId?: number;
    params: Record<string, unknown>;
  },
): Promise<{
  result: Record<string, unknown>;
}> => {
  const {
    app,
    action,
    actionType,
    authenticationId,
    params: bodyParams,
  } = params;

  const invokeRequest: InvokeRequest = {
    selected_api: app,
    type_of: actionType,
    action,
    params: prepareParamsForSend(bodyParams, action),
    authentication_id: authenticationId,
    is_sampling: true,
    test_poll: true,
    sync: false,
    is_bulk_read: false,
    destination_selected_apis: [],
  };

  // Get live results
  const {
    data: invokeResponse,
    error,
    response,
  } = (await facade.api.POST(
    // @ts-expect-error -- this endpoint is actually in "conjurer" so we don't have types for it...
    "/api/v4/implementations/invoke-sync/",
    {
      body: invokeRequest,
    },
  )) as {
    data: components["schemas"]["InvokeResponse"];
    error: { errors: string[] } | undefined;
    response: Response;
  };

  if (error || !invokeResponse.success) {
    throw new ZapierFacadeError(
      "/api/v4/implementations/invoke-sync/",
      "POST",
      invokeRequest,
      response,
      error?.errors,
    );
  }

  const results = invokeResponse.results || [];
  const sampleResult: Record<string, unknown> = {};

  // Take the last result and flatten it
  if (results.length > 0) {
    const lastResult = results[results.length - 1];
    Object.assign(sampleResult, complexFlatten(lastResult));
  }

  return {
    result: sampleResult,
  };
};

export const getActionInfo = async (
  facade: AgentsZapierFacade,
  params: {
    app: string;
    action: string;
    actionType: ActionType;
  },
): Promise<{
  actionInfo: ActionSchema;
  implementation: components["schemas"]["Implementation"];
}> => {
  const { app, action, actionType } = params;
  const implementation = await getImplementation(facade, {
    selectedApi: app,
  });
  if (!implementation) {
    throw new Error(`App ${app} not found`);
  }

  const { action: actionInfo } = getActionFromImplementation(
    implementation,
    action,
    actionType,
  );

  if (!actionInfo) {
    throw new Error(`Action ${app}:${action} not found`);
  }

  return { actionInfo, implementation };
};

export const getActionInfoAndNeeds = async (
  facade: AgentsZapierFacade,
  args: {
    app: string;
    action: string;
    actionType: ActionType;
    authenticationId?: ConnectionId;
    params: Record<string, unknown>;
  },
): Promise<ActionInfoSchema> => {
  const { app, action, actionType, authenticationId, params } = args;

  const implementation = await getImplementation(facade, { selectedApi: app });
  if (!implementation) {
    throw new Error(`App ${app} not found`);
  }

  const { action: actionInfo, needs: actionNeeds } =
    getActionFromImplementation(implementation, action, actionType);

  if (!actionInfo) {
    throw new Error(`Action ${app}:${action} not found`);
  }

  let needs: NeedSchema[] = actionNeeds ?? [];

  const appNeedsAuth = implementationNeedsAuth(implementation);

  // only fetch live needs if we have auth or the app doesn't need auth
  if (authenticationId || !appNeedsAuth) {
    needs = await getNeeds(facade, {
      selectedApi: implementation.selected_api,
      action,
      actionType,
      params,
      authenticationId,
    });
  }

  return {
    action: actionInfo,
    needs,
  };
};

export const getActionGives = async (
  facade: AgentsZapierFacade,
  params: {
    app: string;
    action: string;
    actionType: ActionType;
    authenticationId?: ConnectionId;
    params: Record<string, unknown>;
  },
): Promise<components["schemas"]["Give"][]> => {
  const {
    app,
    action,
    actionType,
    authenticationId,
    params: bodyParams,
  } = params;

  const givesRequest: GivesRequest = {
    selected_api: app,
    type_of: actionType,
    action,
    params: prepareParamsForSend(bodyParams),
    authentication_id: authenticationId,
  };

  const { data, error, response } = await facade.api.POST(
    "/api/v4/implementations/gives/",
    { body: givesRequest },
  );

  if (error) {
    throw new ZapierFacadeError(
      "/api/v4/implementations/gives/",
      "POST",
      params,
      response,
      error?.errors,
    );
  }

  return data.gives || [];
};

export interface ControlledAppsResponse {
  /** List of apps that are controlled for the current user */
  apps: components["schemas"]["AppAccessControl"][];

  /** Strategy for the account to use when controlling apps */
  strategy: components["schemas"]["AppAccessControlStrategy"];
}

export const getControlledApps = async (
  facade: AgentsZapierFacade,
  {
    accountId,
    customUserId,
  }: {
    accountId: number;
    customUserId: number;
  },
): Promise<ControlledAppsResponse> => {
  const [strategyResult, appsResult] = await Promise.all([
    facade.api.GET("/api/v1/controlled-apps-strategies/", {
      params: {
        // @ts-expect-error -- missing type
        query: {
          account_id: accountId.toString(),
        },
      },
    }),

    facade.api.GET("/api/v4/accounts/{account_pk}/controlled-apps/", {
      params: {
        path: {
          account_pk: accountId.toString(),
        },
        query: {
          limit: 10000,
        },
      },
    }),
  ]);

  const {
    data: strategy,
    error: strategyError,
    response: strategyResponse,
  } = strategyResult;
  const { data: apps, error: appsError, response: appsResponse } = appsResult;

  if (strategyError) {
    throw new ZapierFacadeError(
      `/api/v1/controlled-apps-strategies/`,
      "GET",
      { account_id: accountId.toString() },
      strategyResponse,
      strategyError,
    );
  }

  if (appsError) {
    throw new ZapierFacadeError(
      `/api/v4/accounts/${accountId}/controlled-apps`,
      "GET",
      {
        path: {
          account_pk: accountId.toString(),
        },
        query: {
          limit: 10000,
          customuser_id: customUserId,
        },
      },
      appsResponse,
      appsError,
    );
  }

  return {
    // type from monolith is incorrect here
    apps:
      (
        apps as unknown as {
          results: components["schemas"]["AppAccessControl"][];
        }
      ).results || [],
    strategy,
  };
};
